import { useState, useEffect, useRef } from 'react'
import { X, Send, Loader2 } from 'lucide-react'
import { getReferrer } from '@/utils/referrer'

interface InquiryModalProps {
  type: 'DEMO' | 'INQUIRY'
  onClose: () => void
}

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11)
  if (digits.length <= 3) return digits
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`
}

export default function InquiryModal({ type, onClose }: InquiryModalProps) {
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const overlayRef = useRef<HTMLDivElement>(null)

  const isDemo = type === 'DEMO'
  const modalTitle = isDemo ? '무료 데모 신청' : '문의하기'
  const defaultTitle = isDemo ? 'AIQ Lab 무료 데모 신청합니다' : 'AIQ 솔루션 도입을 문의합니다.'

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose()
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhone(e.target.value))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const apiBase = import.meta.env.VITE_API_BASE_URL || '/api'
      const res = await fetch(`${apiBase}/v1/public/landing-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title || defaultTitle,
          content,
          email,
          phone,
          referrer: getReferrer(),
          type,
        }),
      })

      if (!res.ok) {
        const text = await res.text()
        throw new Error(text || `요청 실패 (${res.status})`)
      }

      setSuccess(true)
      setTimeout(() => onClose(), 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류가 발생했습니다. 다시 시도해주세요.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{
        background: 'rgba(0, 0, 0, 0.55)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        animation: 'fadeIn 0.2s ease-out',
      }}
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(24px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
      `}</style>

      <div
        className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
        style={{ animation: 'slideUp 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)' }}
      >
        {/* Header gradient bar */}
        <div className="h-1.5 bg-gradient-to-r from-[#004098] to-[#00a0e9]" />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          aria-label="닫기"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-8 pt-6 pb-8">
          {/* Title */}
          <div className="mb-6">
            <span className="inline-block text-xs font-semibold text-[#00a0e9] uppercase tracking-widest mb-2">
              {isDemo ? 'Free Demo' : 'Contact Us'}
            </span>
            <h2 className="text-xl font-extrabold text-[#161519]">{modalTitle}</h2>
            <p className="mt-1 text-sm text-gray-400">
              {isDemo
                ? '전문 컨설턴트가 맞춤형 데모를 제공합니다.'
                : 'AIQ 솔루션에 대해 궁금하신 점을 문의해 주세요.'}
            </p>
          </div>

          {success ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#004098] to-[#00a0e9] flex items-center justify-center mb-4 shadow-lg shadow-[#004098]/20">
                <Send className="w-7 h-7 text-white" />
              </div>
              <p className="text-lg font-bold text-[#004098]">문의가 접수되었습니다!</p>
              <p className="mt-1 text-sm text-gray-400">빠른 시일 내에 연락드리겠습니다.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Title field */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                  제목 <span className="text-[#00a0e9]">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder={defaultTitle}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-[#161519] placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00a0e9]/30 focus:border-[#00a0e9] transition-all"
                />
              </div>

              {/* Content field */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                  문의 내용 <span className="text-[#00a0e9]">*</span>
                </label>
                <textarea
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  placeholder={isDemo
                    ? '회사명, 인원 규모, 원하시는 데모 일정 등을 알려주세요.'
                    : '문의하시는 담당자 이름, 회사(기관명), 문의 내용을 자세히 작성해 주세요.'}
                  required
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-[#161519] placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00a0e9]/30 focus:border-[#00a0e9] transition-all resize-none"
                />
              </div>

              {/* Email + Phone row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                    이메일 <span className="text-[#00a0e9]">*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="example@company.com"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-[#161519] placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00a0e9]/30 focus:border-[#00a0e9] transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                    연락처 <span className="text-[#00a0e9]">*</span>
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="010-0000-0000"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-[#161519] placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00a0e9]/30 focus:border-[#00a0e9] transition-all"
                  />
                </div>
              </div>

              {/* Error */}
              {error && (
                <p className="text-sm text-red-500 bg-red-50 px-4 py-2.5 rounded-xl">{error}</p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-[#004098] to-[#00a0e9] text-white font-semibold text-sm hover:shadow-lg hover:shadow-[#004098]/25 hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    전송 중...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    {modalTitle}
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
